import React from 'react';
import {
  Form,
  Grid,
  Input,
  Tooltip,
} from '@mairistem/composants';
import { Combobox, Date, Icon } from '@mairistem/finances-utils';
import {
  find,
  startsWith,
  toNumber,
  isEmpty,
  isFinite,
} from 'lodash';
import { toast } from 'react-toastify';
import { ErrorField } from '@mairistem/liste';
import { NatureIdentification, TYPE_TIERS_DEBITEUR_FOURNISSEUR } from '@mairistem/comptabilite';
import TooltipInput from '../TooltipInput';
import ClassificationInput from '../../Classification/ClassificationInput';
import TiersModalNaturesJuridiquesCategoriesPes from '../../CategoriePES/ModalNatJuridiquesCatPes';
import TIERS_TYPES_OPTIONS from '../../../../constants/tiers/tiersTypes';
import NATURE_IDENTIFICATION_OPTIONS from '../../../../constants/tiers/natureIdentifications';
import getCivilites from '../../../../services/tiers/civilite';
import naturesJuridiquesPes from '../../../../constants/tiers/naturesJuridiquesPes';
import categoriesPes from '../../../../constants/tiers/categoriesPes';
import { ComboboxOption } from '../../../../interfaces/common/ComboxOption';

const getError = (errors, id) => find(errors, (e) => e.id === id);

interface TiersFichePanelIdentificationProps {
  identifiantCategorie: number;
  debiteurOuFournisseur: number,
  natureJuridiquePes: string,
  categoriePes: string,
  nom: string,
  complementNom: string,
  identifiantCivilite: number,
  dateNaissance: string,
  prenom: string,
  natureIdentification: string,
  identification: string,
  codeInterne: number,
  reference: string,
  onChange: any,
  identifiant: number | string,
  errors: ErrorField[],
}

const getLabelFromValue = (list: ComboboxOption[], value: string) => {
  const item = list.find((entry) => entry.value === value);
  return item ? item.text : '';
};

const TiersFichePanelIdentification = ({
  identifiantCategorie,
  debiteurOuFournisseur,
  natureJuridiquePes = '',
  categoriePes = '',
  nom = '',
  complementNom = '',
  identifiantCivilite,
  dateNaissance = '',
  prenom = '',
  natureIdentification = '',
  identification = '',
  codeInterne = 0,
  reference = '',
  onChange,
  identifiant,
  errors,
}: TiersFichePanelIdentificationProps) => {
  const [civilites, setCivilites] = React.useState([]);
  const personnePhysique = startsWith(categoriePes, '01');

  React.useEffect(() => {
    if (personnePhysique) {
      getCivilites()
        .then(({ data }) => {
          setCivilites(data);
        }).catch(() => {
          toast.error('Erreur lors de la récupération des civilités.');
        });
    }
  }, [personnePhysique]);

  const [openNatJurModal, setOpenNatJurModal] = React.useState(false);

  const handleChangeNatJurCatPes = React.useCallback(({
    natureJuridiquePes: natJur,
    categoriePes: catPes,
  }) => {
    setOpenNatJurModal(false);
    if (onChange) {
      onChange({
        identifiant,
        natureJuridiquePes: natJur,
        categoriePes: catPes,
      });
    }
  }, [identifiant, onChange]);

  const handleChange = (e, { name, value }) => {
    if (onChange) { onChange({ [name]: value }); }
  };

  const handleChangeDate = (value: string, name: string) => onChange?.({ [name]: value });

  const handleChangeCodeInterne = React.useCallback(({ target }) => {
    const { name, value } = target;

    if (onChange) {
      onChange({
        identifiant,
        [name]: isFinite(toNumber(value)) ? toNumber(value) : 0,
      });
    }
  }, [identifiant, onChange]);

  const handleChangeClassification = React.useCallback((categorie) => {
    const {
      identifiant: idCategorie,
    } = categorie;

    if (onChange) {
      onChange({
        identifiant,
        identifiantCategorie: idCategorie,
      });
    }
  }, [identifiant, onChange]);

  const getIdentificationInputLength = (): number | null => {
    switch (natureIdentification) {
      case NatureIdentification.SIRET:
      case NatureIdentification.TVA_INTRA_COMMUNAUTAIRE:
        return 14;
      case NatureIdentification.RIDET:
      case NatureIdentification.SIREN:
        return 9;
      case NatureIdentification.FINESS:
        return 11;
      case NatureIdentification.NIR:
        return 15;
      case NatureIdentification.TAHITI:
        return 8;
      default:
        return null;
    }
  };

  const handleOpenNatJurModal = () => setOpenNatJurModal(true);

  const handleCloseNatJurModal = () => setOpenNatJurModal(false);

  return (
    <Form>
      <Grid>
        <Grid.Row>
          <Grid.Column width={4}>
            <Form.Field
              control={Combobox}
              key="debiteurOuFournisseur"
              label="Type du tiers"
              // @ts-expect-error
              name="debiteurOuFournisseur"
              onChange={handleChange}
              options={TIERS_TYPES_OPTIONS}
              selection
              value={debiteurOuFournisseur ?? TYPE_TIERS_DEBITEUR_FOURNISSEUR}
              placeholder="Type du tiers"
              required
            />
          </Grid.Column>
          <Grid.Column width={12}>
            <TooltipInput
              required
              id="Identification-natureJuridiquePes/categoriePes"
              key="natureJuridiquePes/categoriePes"
              name="natureJuridiquePes/categoriePes"
              label="Nature juridique / Catégorie"
              placeholder="Nature juridique / Catégorie"
              // @ts-expect-error
              readOnly
              value={isEmpty(natureJuridiquePes) || isEmpty(categoriePes)
                ? ''
                : `${getLabelFromValue(naturesJuridiquesPes, natureJuridiquePes)} / ${getLabelFromValue(categoriesPes, categoriePes)}`}
              icon={<Icon name="search" bordered link onClick={handleOpenNatJurModal} />}
              position="top center"
              error={getError(errors, 'Identification-natureJuridiquePes/categoriePes')}
            />
          </Grid.Column>
        </Grid.Row>

        {personnePhysique
          ? (
            <Grid.Row>
              <Grid.Column width={3}>
                <Tooltip
                  trigger={(
                    <Form.Field
                      control={Combobox}
                      // @ts-expect-error
                      id="Identification-civilite"
                      key="civilite"
                      label="Civilité"
                      name="identifiantCivilite"
                      onChange={handleChange}
                      options={civilites?.map((civilite) => (
                        {
                          key: civilite.identifiant,
                          text: civilite.libelle,
                          value: civilite.identifiant,
                        }
                      ))}
                      required
                      selection
                      value={identifiantCivilite}
                    />
                  )}
                />
              </Grid.Column>
              {/* @ts-expect-error */}
              <TooltipInput
                id="Identification-nom"
                key="nom"
                name="nom"
                label="Nom"
                placeholder="Nom"
                value={nom}
                onChange={handleChange}
                required
                error={getError(errors, 'Identification-nom')}
              />
              <Grid.Column width={3}>
                <Input
                  key="complementNom"
                  name="complementNom"
                  label="Complément Nom"
                  placeholder="Complément Nom"
                  value={complementNom}
                  onChange={handleChange}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                {/* @ts-expect-error */}
                <TooltipInput
                  id="Identification-prenom"
                  key="prenom"
                  name="prenom"
                  label="Prénom"
                  placeholder="Prénom"
                  value={prenom}
                  onChange={handleChange}
                  required
                  error={getError(errors, 'Identification-prenom')}
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field
                  control={Date}
                  // @ts-expect-error fluid
                  fluid
                  key="dateNaissance"
                  label="Date de naissance"
                  name="dateNaissance"
                  onChange={handleChangeDate}
                  value={dateNaissance}
                />
              </Grid.Column>
            </Grid.Row>
          )
          : (
            <Grid.Row>
              <Grid.Column width={16}>
                {/* @ts-expect-error */}
                <TooltipInput
                  id="Identification-nom"
                  key="nom"
                  name="nom"
                  label="Raison sociale"
                  placeholder="Raison sociale"
                  value={nom}
                  onChange={handleChange}
                  error={getError(errors, 'Identification-nom')}
                  required
                />
              </Grid.Column>
            </Grid.Row>
          )}
        <Grid.Row>
          <Grid.Column width={3}>
            <Tooltip
              trigger={(
                <Form.Field
                  control={Combobox}
                  // @ts-expect-error
                  id="Identification-natureIdentification"
                  key="natureIdentification"
                  label="Nature Identifiant"
                  name="natureIdentification"
                  onChange={handleChange}
                  options={NATURE_IDENTIFICATION_OPTIONS}
                  selection
                  value={natureIdentification}
                />
              )}
              position="top center"
              // @ts-expect-error
              error={getError(errors, 'Identification-natureIdentification')}
            />
          </Grid.Column>
          <Grid.Column width={3}>
            {/* @ts-expect-error */}
            <TooltipInput
              id="Identification-identification"
              key="identification"
              name="identification"
              label="Identifiant"
              placeholder="Identifiant"
              value={identification}
              onChange={handleChange}
              error={getError(errors, 'Identification-identification')}
              flowing
              maxLength={getIdentificationInputLength()}
            />
          </Grid.Column>
          <Grid.Column width={3}>
            {/* @ts-expect-error */}
            <TooltipInput
              key="reference"
              name="reference"
              label="Référence"
              placeholder="Référence"
              value={reference}
              onChange={handleChange}
              description="Référence attribuée au tiers par l'ordonnateur"
            />
          </Grid.Column>
          <Grid.Column width={3}>
            <Input
              key="codeInterne"
              name="codeInterne"
              label="Code Interne"
              placeholder="Code Interne"
              value={codeInterne === 0 ? '' : codeInterne}
              onChange={handleChangeCodeInterne}
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <Tooltip
              trigger={(
                <ClassificationInput
                  identifiantCategorie={identifiantCategorie}
                  onChange={handleChangeClassification}
                />
              )}
              position="top left"
              description="Catégorie de regroupement du tiers"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {openNatJurModal && (
        <TiersModalNaturesJuridiquesCategoriesPes
          onClose={handleChangeNatJurCatPes}
          onCancel={handleCloseNatJurModal}
          natureJuridiquePes={natureJuridiquePes}
          categoriePes={categoriePes}
        />
      )}
    </Form>
  );
};

export default TiersFichePanelIdentification;
